import type { LocationBranding } from '@common/location-layer/types/location'

type BrandingInput = Partial<LocationBranding> | null | undefined

/**
 * Resolve the portal **logo** for header/lockup usage.
 *
 * Uses the dedicated logo asset first, then the favicon. Deliberately never
 * falls back to `portalImage` — that is the sign-in/sign-up hero artwork, not a
 * logo, and using it here made the large hero image render as the header logo.
 */
export const resolvePortalLogo = (branding: BrandingInput): string | null => branding?.logo || branding?.favicon || null

/**
 * Resolve the auth **hero** image for the sign-in/sign-up art panel.
 *
 * Returns the tenant `portalImage` when set, else `null`. There is no bundled
 * default art fallback: when no portal image exists the auth layout switches to
 * the centered-card + brand-gradient shell instead of showing generic artwork.
 */
export const resolvePortalHeroImage = (branding: BrandingInput): string | null => branding?.portalImage || null

/** Hex colours (#rgb, #rrggbb, #rrggbbaa) — the format the branding UI emits. */
const HEX_COLOR_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/

export const isSafeBrandColor = (value: string | null | undefined): value is string =>
  typeof value === 'string' && HEX_COLOR_RE.test(value.trim())

/**
 * Resolve the full-viewport background for the centered-card auth shell.
 *
 * Builds a brand gradient from `brandColor1` -> `brandColor2` only when both are
 * safe hex colours, else the default gradient token. Values are tenant-controlled
 * and interpolated into a CSS string, so anything that is not a strict hex colour
 * fails closed to the token — this blocks CSS/style injection on the (unauthed)
 * login page and also avoids a broken gradient from malformed values.
 */
export const resolveAuthBackgroundStyle = (branding: BrandingInput): string =>
  isSafeBrandColor(branding?.brandColor1) && isSafeBrandColor(branding?.brandColor2)
    ? `linear-gradient(180deg, ${branding.brandColor1} 0%, ${branding.brandColor2} 64.42%)`
    : 'var(--cp-auth-default-gradient)'

/**
 * Unique http(s) origins of the tenant branding images (favicon, logo, portal
 * image). Used to emit `preconnect` resource hints so the DNS/TCP/TLS handshake
 * to each CDN is warm before the favicon/logo/hero fetches start. Relative,
 * empty, or malformed values (e.g. the bundled `/favicon-safari.png`) yield no
 * origin and are skipped.
 */
export const resolveBrandingAssetOrigins = (branding: BrandingInput): string[] => {
  const origins = new Set<string>()
  for (const value of [branding?.favicon, branding?.logo, branding?.portalImage]) {
    if (typeof value !== 'string' || value.trim() === '') continue
    try {
      const { origin, protocol } = new URL(value)
      if (protocol === 'http:' || protocol === 'https:') origins.add(origin)
    } catch {
      // Relative or malformed URL — no absolute origin to preconnect to; skip it.
      continue
    }
  }
  return [...origins]
}

/** A `preload as=image` head link for the auth hero (see `buildHeroPreloadLinks`). */
export interface HeroPreloadLink {
  key: string
  rel: 'preload'
  as: 'image'
  href: string
  media: string
  fetchpriority: 'high'
}

/**
 * Head `link` entries that preload the portal hero image as the desktop LCP.
 *
 * Returns a single high-priority `preload as=image` gated to `>= lg` (1024px,
 * matching the split shell's `lg:` breakpoint) so mobile — where the hero is
 * hidden and the centered-card fallback renders — never downloads it. Empty when
 * there is no hero image (fallback shell), so nothing is preloaded.
 */
export const buildHeroPreloadLinks = (heroImage: string | null): HeroPreloadLink[] =>
  heroImage
    ? [{ key: 'cp-auth-hero-preload', rel: 'preload', as: 'image', href: heroImage, media: '(min-width: 1024px)', fetchpriority: 'high' }]
    : []
